/**
 * Bulk Schedule Dialog for Story List
 *
 * Multi-language architecture means phase lives on story_translations.
 * When scheduling stories, we:
 * 1. Update stories.published_at with calculated dates
 * 2. Update story_translations.phase to SCHEDULED for ALL translations
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
// Issue #640: Renamed from StoryPhase to StoryTransPhase to clarify scope
import {
  logger,
  getNextMatchingWeekday,
  toDbKeys,
  withUpdateTimestamp,
  STORY_TRANS_PHASE_CONFIG,
  type StoryTransPhase,
} from '@musedrops/shared';
import { supabase } from '../../../services/supabase';
import { configApi } from '../../../services/configApi';
import { getShowSchedule } from '../../../utils/showScheduling';
import { PhaseChip } from './PhaseChip';

interface ShowInfo {
  slug: string;
  customPublishWeekdays?: string | null;
}

interface StoryWithTranslations {
  id: string;
  slug: string;
  showSlug: string;
  publishedAt: string | null;
  show: ShowInfo;
  // Aggregated phase from translations
  effectivePhase: StoryTransPhase | null;
}

interface StoryCategorization {
  liveCount: number;
  readyCount: number;
  scheduledCount: number;
  liveStories: StoryWithTranslations[];
  readyStories: StoryWithTranslations[];
  scheduledStories: StoryWithTranslations[];
}

interface BulkScheduleDialogProps {
  open: boolean;
  selectedIds: string[];
  onClose: () => void;
  onComplete: () => void;
}

/**
 * Dialog for bulk scheduling stories with automatic date calculation
 *
 * Key behavior:
 * - Categorizes stories by their translation phases
 * - Calculates publish dates based on show's schedule configuration
 * - Updates both stories.published_at and story_translations.phase
 */
export const BulkScheduleDialog = ({
  open,
  selectedIds,
  onClose,
  onComplete,
}: BulkScheduleDialogProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [stories, setStories] = useState<StoryWithTranslations[]>([]);
  const [showsInSelection, setShowsInSelection] = useState<ShowInfo[]>([]);
  const [selectedShow, setSelectedShow] = useState<string>('');
  const [categorization, setCategorization] =
    useState<StoryCategorization | null>(null);

  // Form state
  const [mode, setMode] = useState<'skip' | 'reschedule'>('skip');
  const [startingDate, setStartingDate] = useState<Date | null>(null);
  const [summary, setSummary] = useState<string>('');

  // Load stories when dialog opens
  useEffect(() => {
    const loadStories = async () => {
      if (!open || selectedIds.length === 0) return;

      setIsLoading(true);
      setError(null);

      try {
        // Fetch stories with show info and translations
        const { data, error: fetchError } = await supabase
          .from('stories')
          .select(
            `
            id,
            slug,
            show_slug,
            published_at,
            shows!inner (
              slug,
              custom_publish_weekdays
            )
          `
          )
          .in('id', selectedIds);

        if (fetchError) {
          throw new Error(`Failed to fetch stories: ${fetchError.message}`);
        }

        if (!data || data.length === 0) {
          setStories([]);
          return;
        }

        // Get story slugs to fetch translations
        const storySlugs = data.map(s => s.slug).filter(Boolean);

        // Fetch all translations for these stories
        const { data: translations, error: transError } = await supabase
          .from('story_translations')
          .select('story_slug, phase')
          .in('story_slug', storySlugs);

        if (transError) {
          throw new Error(
            `Failed to fetch translations: ${transError.message}`
          );
        }

        // Build phase map: story_slug -> most advanced phase
        const phaseMap = new Map<string, StoryTransPhase | null>();
        for (const trans of translations ?? []) {
          const currentPhase = phaseMap.get(trans.story_slug);
          // Use the "most advanced" phase (PUBLISHED > SCHEDULED > READY_FOR_PUBLISH > others)
          // For scheduling, we care about READY_FOR_PUBLISH and SCHEDULED
          if (!currentPhase || getPhaseWeight(trans.phase) > getPhaseWeight(currentPhase)) {
            phaseMap.set(trans.story_slug, trans.phase);
          }
        }

        // Transform to our format
        const transformedStories: StoryWithTranslations[] = data.map(row => {
          const show = row.shows as unknown as {
            slug: string;
            custom_publish_weekdays: string | null;
          };
          return {
            id: row.id,
            slug: row.slug,
            showSlug: row.show_slug,
            publishedAt: row.published_at,
            show: {
              slug: show.slug,
              customPublishWeekdays: show.custom_publish_weekdays,
            },
            effectivePhase: phaseMap.get(row.slug) || null,
          };
        });

        setStories(transformedStories);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        logger.error('Failed to load stories for scheduling', { error: message });
      } finally {
        setIsLoading(false);
      }
    };

    loadStories();
  }, [open, selectedIds]);

  // Extract unique shows when stories load
  useEffect(() => {
    if (!stories.length) return;

    const uniqueShows = new Map<string, ShowInfo>();
    stories.forEach(story => {
      if (story.show && !uniqueShows.has(story.show.slug)) {
        uniqueShows.set(story.show.slug, story.show);
      }
    });

    const showList = Array.from(uniqueShows.values());
    setShowsInSelection(showList);

    // Select first show by default
    const firstShow = showList[0];
    if (firstShow && !selectedShow) {
      setSelectedShow(firstShow.slug);
    }
  }, [stories, selectedShow]);

  // Categorize stories when show selection changes
  useEffect(() => {
    if (!stories.length || !selectedShow) return;

    const showStories = stories.filter(s => s.show?.slug === selectedShow);

    const liveStories: StoryWithTranslations[] = [];
    const readyStories: StoryWithTranslations[] = [];
    const scheduledStories: StoryWithTranslations[] = [];

    showStories.forEach(story => {
      if (story.effectivePhase === 'PUBLISHED') {
        liveStories.push(story);
      } else if (story.effectivePhase === 'READY_FOR_PUBLISH') {
        readyStories.push(story);
      } else if (story.effectivePhase === 'SCHEDULED') {
        scheduledStories.push(story);
      }
    });

    setCategorization({
      liveCount: liveStories.length,
      readyCount: readyStories.length,
      scheduledCount: scheduledStories.length,
      liveStories,
      readyStories,
      scheduledStories,
    });

    // Auto-select reschedule mode if there are only scheduled stories
    if (readyStories.length === 0 && scheduledStories.length > 0) {
      setMode('reschedule');
    } else {
      setMode('skip');
    }
  }, [stories, selectedShow]);

  // Calculate default starting date when categorization loads
  useEffect(() => {
    const calculateDefaultDate = async () => {
      if (!categorization || !selectedShow || !showsInSelection.length) return;

      const show = showsInSelection.find(s => s.slug === selectedShow);
      if (!show) return;

      const storiesToSchedule: StoryWithTranslations[] = [
        ...categorization.readyStories,
        ...(mode === 'reschedule' ? categorization.scheduledStories : []),
      ];

      if (storiesToSchedule.length === 0) return;

      try {
        // Query latest future date from this show (excluding stories being rescheduled)
        const excludeIds = storiesToSchedule.map(s => s.id);
        const now = new Date().toISOString();

        let query = supabase
          .from('stories')
          .select('published_at')
          .eq('show_slug', selectedShow)
          .gte('published_at', now);

        if (excludeIds.length > 0) {
          query = query.not('id', 'in', `("${excludeIds.join('","')}")`);
        }

        const { data: latestStory } = await query
          .order('published_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        const defaultDate = latestStory?.published_at
          ? new Date(latestStory.published_at)
          : new Date();

        setStartingDate(defaultDate);
      } catch (err) {
        logger.error('Failed to calculate default starting date', { error: err });
      }
    };

    calculateDefaultDate();
  }, [categorization, selectedShow, showsInSelection, mode]);

  // Calculate publish dates
  const calculatePublishDates = useCallback(
    async (
      show: ShowInfo,
      storiesToSchedule: StoryWithTranslations[],
      userStartingDate?: Date | null
    ): Promise<Map<string, string>> => {
      const dates = new Map<string, string>();

      try {
        const { config } = await configApi.getAppConfig();
        const schedule = getShowSchedule(show, config);
        const { weekdays, publishHour } = schedule;

        let baseline: Date;

        if (userStartingDate) {
          baseline = new Date(userStartingDate);
        } else {
          const excludeIds = storiesToSchedule.map(s => s.id);
          const now = new Date().toISOString();

          let query = supabase
            .from('stories')
            .select('published_at')
            .eq('show_slug', selectedShow)
            .gte('published_at', now);

          if (excludeIds.length > 0) {
            query = query.not('id', 'in', `("${excludeIds.join('","')}")`);
          }

          const { data: latestStory } = await query
            .order('published_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          baseline = latestStory?.published_at
            ? new Date(latestStory.published_at)
            : new Date();
        }

        for (const story of storiesToSchedule) {
          baseline = getNextMatchingWeekday(baseline, weekdays);
          baseline.setUTCHours(publishHour, 0, 0, 0);

          if (!userStartingDate) {
            const nowCheck = new Date();
            if (baseline <= nowCheck) {
              baseline.setUTCDate(baseline.getUTCDate() + 1);
              baseline = getNextMatchingWeekday(baseline, weekdays);
              baseline.setUTCHours(publishHour, 0, 0, 0);
            }
          }

          dates.set(story.id, baseline.toISOString());
        }

        return dates;
      } catch (err) {
        logger.error('Failed to calculate publish dates', { error: err });
        return dates;
      }
    },
    [selectedShow]
  );

  // Calculate summary when categorization or mode changes
  useEffect(() => {
    const calculateSummary = async () => {
      if (!categorization || !selectedShow) return;

      const storiesToSchedule: StoryWithTranslations[] = [
        ...categorization.readyStories,
        ...(mode === 'reschedule' ? categorization.scheduledStories : []),
      ];

      if (storiesToSchedule.length === 0) {
        setSummary('');
        return;
      }

      try {
        const show = showsInSelection.find(s => s.slug === selectedShow);
        if (!show) return;

        const { config } = await configApi.getAppConfig();
        const schedule = getShowSchedule(show, config);
        const { weekdays } = schedule;

        const weekdayNames: Record<string, string> = {
          sun: 'Sunday',
          mon: 'Monday',
          tue: 'Tuesday',
          wed: 'Wednesday',
          thu: 'Thursday',
          fri: 'Friday',
          sat: 'Saturday',
        };
        const weekdaysText = weekdays.map(d => weekdayNames[d]).join(', ');

        const dates = await calculatePublishDates(
          show,
          storiesToSchedule,
          startingDate
        );
        const firstDate = dates.values().next().value;
        const formattedDate = firstDate
          ? new Date(firstDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
          : '[calculating...]';

        setSummary(
          `Will schedule **${storiesToSchedule.length} stories** on **${weekdaysText}** starting **${formattedDate}**`
        );
      } catch (err) {
        logger.error('Failed to calculate summary', { error: err });
        setSummary('');
      }
    };

    calculateSummary();
  }, [
    categorization,
    mode,
    selectedShow,
    showsInSelection,
    startingDate,
    calculatePublishDates,
  ]);

  const handleClose = () => {
    if (!isProcessing) {
      setStories([]);
      setShowsInSelection([]);
      setSelectedShow('');
      setCategorization(null);
      setMode('skip');
      setStartingDate(null);
      setSummary('');
      setError(null);
      onClose();
    }
  };

  const handleSchedule = async () => {
    if (!categorization) return;

    const storiesToSchedule: StoryWithTranslations[] = [
      ...categorization.readyStories,
      ...(mode === 'reschedule' ? categorization.scheduledStories : []),
    ];

    if (storiesToSchedule.length === 0) {
      setError('No stories to schedule');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const show = showsInSelection.find(s => s.slug === selectedShow);
      if (!show) {
        throw new Error('Show not found');
      }

      // Calculate all dates upfront
      const dates = await calculatePublishDates(
        show,
        storiesToSchedule,
        startingDate
      );

      if (dates.size === 0) {
        throw new Error('Failed to calculate publish dates');
      }

      // Get story slugs for translation updates
      const storySlugs = storiesToSchedule
        .map(s => s.slug)
        .filter(Boolean) as string[];

      // Step 1: Update story_translations.phase to SCHEDULED for ALL translations
      const { error: translationError } = await supabase
        .from('story_translations')
        .update(
          withUpdateTimestamp(
            toDbKeys({
              phase: 'SCHEDULED',
            })
          )
        )
        .in('story_slug', storySlugs);

      if (translationError) {
        throw new Error(
          `Failed to update translation phases: ${translationError.message}`
        );
      }

      // Step 2: Update stories.published_at for each story
      let successCount = 0;
      let errorCount = 0;

      for (const story of storiesToSchedule) {
        const publishedAt = dates.get(story.id);
        if (!publishedAt) {
          errorCount++;
          continue;
        }

        const { error: storyError } = await supabase
          .from('stories')
          .update(
            withUpdateTimestamp(
              toDbKeys({
                publishedAt,
              })
            )
          )
          .eq('id', story.id);

        if (storyError) {
          errorCount++;
          logger.error('Failed to update story', {
            storyId: story.id,
            error: storyError.message,
          });
        } else {
          successCount++;
        }
      }

      logger.info('Bulk scheduled stories', {
        successCount,
        errorCount,
        translationCount: storySlugs.length,
      });

      if (successCount > 0) {
        handleClose();
        onComplete();
      } else {
        throw new Error('Failed to schedule any stories');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      logger.error('Bulk schedule failed', { error: message });
    } finally {
      setIsProcessing(false);
    }
  };

  const totalToSchedule =
    mode === 'skip'
      ? categorization?.readyCount || 0
      : (categorization?.readyCount || 0) +
        (categorization?.scheduledCount || 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Schedule Stories</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!isLoading && showsInSelection.length > 0 && (
          <>
            {/* Show Selector */}
            {showsInSelection.length > 1 && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Show</InputLabel>
                <Select
                  value={selectedShow}
                  label="Show"
                  onChange={e => setSelectedShow(e.target.value)}
                  disabled={isProcessing}
                >
                  {showsInSelection.map(show => (
                    <MenuItem key={show.slug} value={show.slug}>
                      {show.slug}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {showsInSelection.length === 1 && showsInSelection[0] && (
              <Typography variant="body1" sx={{ mb: 2 }}>
                Show: <strong>{showsInSelection[0].slug}</strong>
              </Typography>
            )}

            {/* Status Counts with Emojis */}
            {categorization && (
              <Box sx={{ mb: 3 }}>
                {categorization.liveCount > 0 && (
                  <PhaseChip
                    phase="PUBLISHED"
                    count={categorization.liveCount}
                    suffix="(Ignored)"
                  />
                )}
                {categorization.readyCount > 0 && (
                  <PhaseChip
                    phase="READY_FOR_PUBLISH"
                    count={categorization.readyCount}
                  />
                )}
                {categorization.scheduledCount > 0 && (
                  <PhaseChip
                    phase="SCHEDULED"
                    count={categorization.scheduledCount}
                  />
                )}
              </Box>
            )}

            {/* Mode Selection (conditional on scheduled > 0) */}
            {categorization && categorization.scheduledCount > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  For already scheduled stories:
                </Typography>
                <RadioGroup
                  row
                  value={mode}
                  onChange={e =>
                    setMode(e.target.value as 'skip' | 'reschedule')
                  }
                >
                  <FormControlLabel
                    value="skip"
                    control={<Radio />}
                    label="Skip"
                    disabled={categorization.readyCount === 0 || isProcessing}
                  />
                  <FormControlLabel
                    value="reschedule"
                    control={<Radio />}
                    label="Reschedule"
                    disabled={isProcessing}
                  />
                </RadioGroup>
              </Box>
            )}

            {/* Starting Date Picker */}
            {categorization && totalToSchedule > 0 && startingDate && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Starting At"
                  type="date"
                  value={
                    startingDate
                      ? new Date(
                          startingDate.getTime() -
                            startingDate.getTimezoneOffset() * 60000
                        )
                          .toISOString()
                          .split('T')[0]
                      : ''
                  }
                  onChange={e => {
                    const dateValue = e.target.value;
                    if (dateValue) {
                      const parts = dateValue.split('-').map(Number);
                      const year = parts[0] ?? 0;
                      const month = parts[1] ?? 1;
                      const day = parts[2] ?? 1;
                      const newDate = new Date(year, month - 1, day);
                      setStartingDate(newDate);
                    }
                  }}
                  fullWidth
                  disabled={isProcessing}
                  helperText="Stories will be scheduled on matching weekdays starting from this date"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            )}

            {/* No stories warning */}
            {categorization && totalToSchedule === 0 && (
              <Alert severity="warning">
                No stories available for scheduling. Select stories in
                READY_FOR_PUBLISH or SCHEDULED phase.
              </Alert>
            )}
          </>
        )}

        {/* Summary Preview */}
        {summary && totalToSchedule > 0 && (
          <Typography
            variant="body2"
            sx={{ px: 3, py: 2, color: 'text.secondary' }}
          >
            {/* Render **bold** markers as <strong> without innerHTML (#748 / I-24) */}
            <span>
              {summary
                .split(/\*\*(.*?)\*\*/g)
                .map((part, index) =>
                  index % 2 === 1 ? <strong key={index}>{part}</strong> : part
                )}
            </span>
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          onClick={handleSchedule}
          variant="contained"
          disabled={
            isProcessing ||
            isLoading ||
            !categorization ||
            totalToSchedule === 0
          }
          startIcon={isProcessing ? <CircularProgress size={16} /> : null}
        >
          {isProcessing
            ? 'Scheduling...'
            : `Schedule ${totalToSchedule} stor${totalToSchedule > 1 ? 'ies' : 'y'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Get weight for phase comparison (higher = more advanced)
 * Issue #616: Derive weights from canonical STORY_TRANS_PHASE_CONFIG order
 * Issue #640: Renamed from StoryPhase to StoryTransPhase
 */
function getPhaseWeight(phase: StoryTransPhase | null): number {
  if (!phase) return 0;
  // Derive weights from canonical phase order in STORY_TRANS_PHASE_CONFIG
  const phases = Object.keys(STORY_TRANS_PHASE_CONFIG) as StoryTransPhase[];
  const index = phases.indexOf(phase);
  // Return 0 for unknown phases, otherwise scale from 10-100
  return index >= 0 ? (index + 1) * 10 : 0;
}
